import React, { useEffect, useState } from "react";
import {
  LuSparkles,
  LuX,
  LuKeyboard,
  LuSave,
  LuMailCheck,
  LuEye,
  LuBell,
  LuCopy,
  LuActivity,
  LuSend,
  LuCalculator,
  LuMaximize2,
  LuPercent,
  LuSigma,
  LuSplit,
  LuPaperclip,
  LuPencil,
  LuTag,
} from "react-icons/lu";
import { NOVIDADES, IconeNovidade } from "../../data/novidades";

/** Mapeia a chave de ícone (dados) para o componente lucide correspondente. */
const ICONES: Record<
  IconeNovidade,
  React.ComponentType<{ className?: string }>
> = {
  teclado: LuKeyboard,
  salvar: LuSave,
  lembrete: LuMailCheck,
  visto: LuEye,
  notificacoes: LuBell,
  duplicar: LuCopy,
  status: LuActivity,
  reenvio: LuSend,
  imposto: LuCalculator,
  janela: LuMaximize2,
  rateio: LuPercent,
  formulas: LuSigma,
  clausula: LuSplit,
  anexo: LuPaperclip,
  observacoes: LuPencil,
  rotulos: LuTag,
};

interface NovidadesModalProps {
  aberto: boolean;
  /** Fecha o modal. `naoMostrarNovamente` reflete o estado do checkbox. */
  onFechar: (naoMostrarNovamente: boolean) => void;
}

/**
 * Pop-up de "Novidades do Sistema" — mostrado ao entrar na listagem de planilhas.
 * Identidade visual da tela de orçamento do fornecedor: fundo escuro #161519,
 * acento verde-limão #d2fa52 e ação primária azul #4760e0. Ícones: lucide.
 * Conteúdo vem de src/data/novidades.ts.
 */
const NovidadesModal: React.FC<NovidadesModalProps> = ({
  aberto,
  onFechar,
}) => {
  const [naoMostrar, setNaoMostrar] = useState(false);

  // Fechar com Esc, respeitando o estado do checkbox.
  useEffect(() => {
    if (!aberto) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onFechar(naoMostrar);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [aberto, naoMostrar, onFechar]);

  if (!aberto) return null;

  const fechar = () => onFechar(naoMostrar);

  return (
    <div
      className="fixed inset-0 z-9999 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={fechar}
      role="dialog"
      aria-modal="true"
      aria-labelledby="novidades-titulo"
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabeçalho — fundo escuro com borda verde-limão (igual ao portal) */}
        <div className="bg-[#161519] border-b-2 border-[#d2fa52] px-6 py-5 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#d2fa52] flex items-center justify-center shrink-0">
              <LuSparkles className="h-6 w-6 text-[#161519]" />
            </div>
            <div>
              <h2
                id="novidades-titulo"
                className="text-xl font-bold text-white leading-tight"
              >
                Novidades do Sistema
              </h2>
              <p className="text-sm text-[#d2fa52] leading-tight">
                O que melhorou pra você usar melhor
              </p>
            </div>
          </div>
          <button
            type="button"
            className="text-white/50 hover:text-white transition-colors p-1"
            onClick={fechar}
            aria-label="Fechar"
          >
            <LuX className="h-6 w-6" />
          </button>
        </div>

        {/* Corpo — lista de versões (mais recente primeiro), com scroll interno */}
        <div className="px-6 py-5 overflow-y-auto">
          {NOVIDADES.map((novidade, idx) => (
            <div
              key={novidade.versao}
              className={idx > 0 ? "mt-8 pt-6 border-t border-gray-200" : ""}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {novidade.data}
                </span>
                {idx === 0 && (
                  <span className="text-[11px] font-bold uppercase tracking-wide text-[#161519] bg-[#d2fa52] px-2 py-0.5 rounded-full">
                    Novo
                  </span>
                )}
              </div>

              <h3 className="text-lg font-bold text-[#161519] mb-4">
                {novidade.titulo}
              </h3>

              <ul className="flex flex-col gap-2.5">
                {novidade.itens.map((item, i) => {
                  const Icone = ICONES[item.icone] ?? LuSparkles;
                  return (
                    <li
                      key={i}
                      className="group flex items-start gap-3 rounded-2xl border border-gray-200/80 bg-white p-3.5 transition-all hover:border-[#d2fa52] hover:shadow-md hover:shadow-[#d2fa52]/10"
                    >
                      <div className="w-10 h-10 rounded-xl bg-linear-to-br from-[#eaffa6] to-[#d2fa52] ring-1 ring-[#d2fa52] flex items-center justify-center shrink-0 shadow-sm transition-transform group-hover:scale-105 group-hover:-rotate-3">
                        <Icone className="h-5 w-5 text-[#161519]" />
                      </div>
                      <div className="min-w-0 pt-0.5">
                        <div className="font-bold text-[#161519]">
                          {item.titulo}
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed mt-0.5">
                          {item.descricao}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Rodapé — checkbox + ação */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-600">
            <input
              type="checkbox"
              className="w-4 h-4 rounded accent-[#4760e0] cursor-pointer"
              checked={naoMostrar}
              onChange={(e) => setNaoMostrar(e.target.checked)}
            />
            Não mostrar novamente
          </label>
          <button
            type="button"
            className="w-full sm:w-auto bg-[#4760e0] hover:bg-[#3a4fc0] text-white font-semibold rounded-lg px-6 py-2.5 transition-colors"
            onClick={fechar}
          >
            Entendi!
          </button>
        </div>
      </div>
    </div>
  );
};

export default NovidadesModal;
